import React, { useState, useEffect } from 'react';
import { getUIConfig, saveUIConfig } from '../services/api';

const useSettings = () => {
  const [colorPalette, setColorPalette] = useState({});
  const [font, setFont] = useState('');
  const [metadata, setMetadata] = useState([]);
  const [bgImgUrl, setBgImgUrl] = useState('');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const transformColorPalette = (palette) => {
    if (!palette) return {};
    let parsedPalette = {};
    if (typeof palette === "string") {
      try {
        parsedPalette = JSON.parse(palette);
      } catch (error) {
        console.error("Error parsing palette:", error);
        return {};
      }
    } else {
      parsedPalette = palette;
    }
    
    return parsedPalette;
  };

  const loadConfigData = async () => {
    setLoading(true);
    try {
      const result = await getUIConfig();
      setConfig(result);
      
      setBgImgUrl(result.backgroundImgUrl || result.background_img_url || "");
      setColorPalette(transformColorPalette(result.colorPalette || result.color_palette));
      setFont(result.font || "");
      
      // Load metadata from localStorage
      const savedMetadata = localStorage.getItem('metadata');
      if (savedMetadata) {
        setMetadata(JSON.parse(savedMetadata));
      }
    } catch (error) {
      console.error("Error loading config:", error);
      setColorPalette({});
      setBgImgUrl('');
      setFont('');
    } finally {
      setLoading(false);
    }
  };

  const saveAppearanceData = async () => {
    setLoading(true);
    try {
      const result = await saveUIConfig({ bgImgUrl, colorPalette, font });
      return result;
    } catch (error) {
      console.error("Error saving appearance config:", error);
      return { success: false, message: "Error saving appearance settings" };
    } finally {
      setLoading(false);
    }
  };

  const saveMetadataData = async () => {
    setLoading(true);
    try {
      // Save metadata to localStorage
      localStorage.setItem('metadata', JSON.stringify(metadata));
      return { success: true, message: "Metadata saved successfully" };
    } catch (error) {
      console.error("Error saving metadata config:", error);
      return { success: false, message: "Error saving metadata settings" };
    } finally {
      setLoading(false);
    }
  };

  const saveZendeskData = async (zendeskCredentials) => {
    setLoading(true);
    try {
      // Save Zendesk credentials to localStorage
      localStorage.setItem('zendeskCredentials', JSON.stringify(zendeskCredentials));
      return { success: true, message: "Zendesk credentials saved successfully" };
    } catch (error) {
      console.error("Error saving Zendesk config:", error);
      return { success: false, message: "Error saving Zendesk credentials" };
    } finally {
      setLoading(false);
    }
  };

  return {
    colorPalette, setColorPalette,
    font, setFont,
    metadata, setMetadata,
    bgImgUrl, setBgImgUrl,
    config,
    loading,
    loadConfigData,
    saveAppearanceData,
    saveMetadataData,
    saveZendeskData
  };
};

const ZendeskCredentials = ({ settings }) => {
  const { saveZendeskData, loading } = settings;
  const [credentials, setCredentials] = useState({
    subdomain: '',
    email: '',
    api_token: ''
  });
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Load existing credentials on component mount
  useEffect(() => {
    const savedCredentials = localStorage.getItem('zendeskCredentials');
    if (savedCredentials) {
      setCredentials(JSON.parse(savedCredentials));
    }
  }, []);

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validate required fields
    if (!credentials.subdomain || !credentials.email || !credentials.api_token) {
      setMessage('All fields are required');
      setMessageType('error');
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
      return;
    }

    const result = await saveZendeskData(credentials);
    setMessage(result.message);
    setMessageType(result.success ? 'success' : 'error');
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
      <h2 className="text-xl font-bold mb-4 text-white border-b border-gray-700 pb-2">Zendesk Credentials</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          messageType === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {message}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">Subdomain</label>
          <input
            type="text"
            className="w-full bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            value={credentials.subdomain}
            onChange={(e) => handleInputChange('subdomain', e.target.value)}
            placeholder="Enter your Zendesk subdomain"
          />
          <p className="text-xs text-gray-400 mt-1">Example: mycompany (from mycompany.zendesk.com)</p>
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">Email</label>
          <input
            type="email"
            className="w-full bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            value={credentials.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="Enter your Zendesk email"
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm font-medium mb-2">API Token</label>
          <input
            type="password"
            className="w-full bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            value={credentials.api_token}
            onChange={(e) => handleInputChange('api_token', e.target.value)}
            placeholder="Enter your Zendesk API token"
          />
          <p className="text-xs text-gray-400 mt-1">You can generate this from your Zendesk Admin settings</p>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button 
          onClick={handleSave}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
        >
          {loading && <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          Save Credentials
        </button>
      </div>
    </div>
  );
};

const Metadata = ({ settings }) => {
  const { metadata, setMetadata, saveMetadataData, loading } = settings;
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const addMetadataField = () => {
    setMetadata([...metadata, { key: '', value: '' }]);
  };

  const removeMetadataField = (index) => {
    const newMetadata = metadata.filter((_, i) => i !== index);
    setMetadata(newMetadata);
  };

  const updateMetadataField = (index, field, value) => {
    const newMetadata = [...metadata];
    newMetadata[index][field] = value;
    setMetadata(newMetadata);
  };

  const handleSave = async () => {
    const result = await saveMetadataData();
    setMessage(result.message);
    setMessageType(result.success ? 'success' : 'error');
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };
  
  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
      <h2 className="text-xl font-bold mb-4 text-white border-b border-gray-700 pb-2">Metadata</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          messageType === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {message}
        </div>
      )}

      <div className="mb-4">
        <div className="flex justify-between items-center mb-3">
          <label className="block text-gray-300 text-sm font-medium">Key-Value Pairs</label>
          <button
            onClick={addMetadataField}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded text-sm"
          >
            Add Field
          </button>
        </div>
        
        {metadata.map((item, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              placeholder="Key"
              className="flex-1 bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              value={item.key}
              onChange={(e) => updateMetadataField(index, 'key', e.target.value)}
            />
            <input
              type="text"
              placeholder="Value"
              className="flex-1 bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
              value={item.value}
              onChange={(e) => updateMetadataField(index, 'value', e.target.value)}
            />
            <button
              onClick={() => removeMetadataField(index)}
              className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm"
            >
              Remove
            </button>
          </div>
        ))}
        
        {metadata.length === 0 && (
          <div className="text-gray-400 text-sm text-center py-4">
            No metadata fields. Click "Add Field" to create one.
          </div>
        )}
      </div>

      <div className="flex justify-end mt-6">
        <button 
          onClick={handleSave}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
        >
          {loading && <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          Save Metadata
        </button>
      </div>
    </div>
  );
};

const Appearance = ({ settings }) => {
  const { colorPalette, setColorPalette, font, setFont, bgImgUrl, setBgImgUrl, saveAppearanceData, loading } = settings;
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const handleSave = async () => {
    const result = await saveAppearanceData();
    setMessage(result.message);
    setMessageType(result.success ? 'success' : 'error');
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 3000);
  };

  const updateColorValue = (key, value) => {
    setColorPalette({...colorPalette, [key]: value});
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6 shadow-lg mb-6">
      <h2 className="text-xl font-bold mb-4 text-white border-b border-gray-700 pb-2">Appearance</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          messageType === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {message}
        </div>
      )}

      <div className="mb-4">
        <label className="block text-gray-300 text-sm font-medium mb-2">Background Image URL</label>
        <input
          type="text"
          className="w-full bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          value={bgImgUrl}
          onChange={(e) => setBgImgUrl(e.target.value)}
          placeholder="Enter background image URL"
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-300 text-sm font-medium mb-3">Color Palette</label>
        
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(colorPalette).map(([key, value]) => (
            <div key={key} className="flex items-center space-x-3 p-3 bg-gray-700 rounded-lg">
              <div
                className="w-8 h-8 rounded cursor-pointer border-2 border-gray-600 flex-shrink-0"
                style={{backgroundColor: value}}
              />
              <div className="flex-1 min-w-0">
                <div className="text-gray-300 text-sm mb-1 truncate">{key}</div>
                <input
                  type="text"
                  placeholder="Color value"
                  className="w-full bg-gray-600 text-white border-none rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-xs"
                  value={value}
                  onChange={(e) => updateColorValue(key, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
        
        {Object.keys(colorPalette).length === 0 && (
          <div className="text-gray-400 text-sm text-center py-4">
            No colors defined in the configuration.
          </div>
        )}
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-300 text-sm font-medium mb-2">Font</label>
        <input
          type="text"
          className="w-full bg-gray-700 text-white border-none rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
          value={font}
          onChange={(e) => setFont(e.target.value)}
          placeholder="Enter font family"
        />
      </div>

      <div className="flex justify-end mt-6">
        <button 
          onClick={handleSave}
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
        >
          {loading && <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          Save Appearance
        </button>
      </div>
    </div>
  );
};

const Settings = () => {
  const settingsHookValues = useSettings();
  const { loading, loadConfigData } = settingsHookValues;

  useEffect(() => {
    loadConfigData();
  }, []);

  return (
    <div className="p-6 bg-[#121721] min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-white">Settings</h1>
      
      {loading && (
        <div className="mb-4 p-3 bg-blue-600 text-white rounded-lg flex items-center">
          <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
          Loading settings...
        </div>
      )}
      
      <ZendeskCredentials settings={settingsHookValues} />
      <Metadata settings={settingsHookValues} />
      <Appearance settings={settingsHookValues} />
    </div>
  );
};

export default Settings;
